import { TrendingUp, TrendingDown, DollarSign, BarChart3, Activity, Crown, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DeepAnalysis } from '@/hooks/useDeepAnalysis';

interface MarketOverviewProps {
  analysis: DeepAnalysis;
}

const formatNumber = (num: number | null | undefined, decimals = 2): string => {
  if (num === null || num === undefined || !Number.isFinite(num)) return 'Unavailable';
  if (num >= 1e12) return `A$${(num / 1e12).toFixed(decimals)}T`;
  if (num >= 1e9) return `A$${(num / 1e9).toFixed(decimals)}B`;
  if (num >= 1e6) return `A$${(num / 1e6).toFixed(decimals)}M`;
  if (num >= 1e3) return `A$${(num / 1e3).toFixed(decimals)}K`;
  return `A$${num.toFixed(decimals)}`;
};

const formatPrice = (price: number): string => {
  if (!Number.isFinite(Number(price))) return 'A$—';
  if (price < 0.01) return `A$${price.toFixed(6)}`;
  if (price < 1) return `A$${price.toFixed(4)}`;
  if (price < 100) return `A$${price.toFixed(2)}`;
  return `A$${price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const PriceChange = ({ value, label }: { value: number; label: string }) => {
  const safeValue = Number.isFinite(Number(value)) ? Number(value) : 0;
  const isPositive = safeValue >= 0;
  return (
    <div className="text-center">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <div className={`flex items-center justify-center gap-1 ${isPositive ? 'text-success' : 'text-destructive'}`}>
        {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        <span className="font-bold">{isPositive ? '+' : ''}{safeValue.toFixed(2)}%</span>
      </div>
    </div>
  );
};

const sourceLabel = (ds?: string) => {
  switch (ds) {
    case 'live_market': return 'Live Market Data';
    case 'toss_canonical': return 'TOSS Snapshot';
    default: return 'TOSS Snapshot';
  }
};

const sourceBadgeVariant = (ds?: string): 'default' | 'secondary' | 'outline' | 'destructive' => {
  switch (ds) {
    case 'live_market': return 'secondary';
    case 'toss_canonical': return 'default';
    default: return 'default';
  }
};


export function MarketOverview({ analysis }: MarketOverviewProps) {
  const marketData = analysis.marketData || {} as DeepAnalysis['marketData'];
  const tokenInfo = analysis.tokenInfo || { symbol: '?', name: 'Unknown token', categories: [] } as DeepAnalysis['tokenInfo'];
  const historicalAth = analysis.historicalIndicators?.ath_price_usd;
  const historicalDrawdown = analysis.historicalIndicators?.ath_drawdown_pct;
  const hasAth = historicalAth !== null && historicalAth !== undefined && historicalAth > 0;
  const isDerived = analysis.historicalAvailability?.source === 'derived_stored_history' || analysis.historicalIndicators?.data_source === 'derived_stored_history';
  const isPartial = analysis.historicalAvailability?.partial || analysis.historicalIndicators?.partial;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary/50 flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-lg">
                {(tokenInfo.symbol || '?').charAt(0)}
              </span>
            </div>
            <div>
              <CardTitle className="text-xl">{tokenInfo.name || 'Unknown token'}</CardTitle>
              <div className="flex items-center gap-2">
                <p className="text-sm text-muted-foreground">{tokenInfo.symbol || '—'}</p>
                <Badge variant={sourceBadgeVariant(analysis.dataSource)} className="text-[10px] px-1.5 py-0">
                  {sourceLabel(analysis.dataSource)}
                </Badge>
              </div>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-foreground">{formatPrice(Number(marketData.price))}</p>
            <div className={`flex items-center justify-end gap-1 ${(marketData.priceChange24h || 0) >= 0 ? 'text-success' : 'text-destructive'}`}>
              {(marketData.priceChange24h || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
              <span className="font-medium">{(marketData.priceChange24h || 0) >= 0 ? '+' : ''}{Number(marketData.priceChange24h || 0).toFixed(2)}%</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-secondary/30 border border-border rounded-lg p-2 text-xs text-muted-foreground flex items-center gap-2">
          <Info className="w-3.5 h-3.5 flex-shrink-0" />
          <span>
            {analysis.dataSource === 'live_market'
              ? `Live market data${analysis.dataAge ? ` as at ${new Date(analysis.dataAge).toLocaleString()}` : ''}. This coin sits outside our daily TOSS scoring, so there's no TOSS score for it.`
              : `TOSS canonical snapshot${analysis.dataAge ? ` from ${new Date(analysis.dataAge).toLocaleString()}` : ''}.`}
            {isDerived ? ' Technical analytics are derived from stored market history.' : isPartial ? ' Partial analytics shown while live candles are unavailable.' : ''}

          </span>
        </div>

        {/* Price Changes */}
        <div className="grid grid-cols-3 gap-4 p-3 bg-secondary/30 rounded-lg">
          <PriceChange value={marketData.priceChange24h} label="24h" />
          <PriceChange value={marketData.priceChange7d} label="7d" />
          <PriceChange value={marketData.priceChange30d} label="30d" />
        </div>

        {/* Market Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
            <DollarSign className="w-5 h-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">Market Cap</p>
              <p className="font-bold text-foreground">{formatNumber(marketData.marketCap)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
            <BarChart3 className="w-5 h-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">24h Volume</p>
              <p className="font-bold text-foreground">{formatNumber(marketData.volume)}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
            <Crown className="w-5 h-5 text-warning" />
            <div>
              <p className="text-xs text-muted-foreground">All-Time High</p>
              {hasAth ? (
                <>
                  <p className="font-bold text-foreground">A${historicalAth.toLocaleString(undefined, { maximumFractionDigits: 6 })}</p>
                  <p className="text-xs text-destructive">{historicalDrawdown?.toFixed(1) ?? 'N/A'}% below ATH</p>
                </>
              ) : (
                <>
                  <p className="font-bold text-foreground">ATH data unavailable from market source</p>
                  <p className="text-xs text-muted-foreground">ATH Recovery Signal: 5/10 (neutral)</p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-card rounded-lg border border-border">
            <Activity className="w-5 h-5 text-primary" />
            <div>
              <p className="text-xs text-muted-foreground">FDV</p>
              <p className="font-bold text-foreground">{formatNumber(marketData.fdv)}</p>
            </div>
          </div>
        </div>

        {/* Categories */}
        {tokenInfo.categories && tokenInfo.categories.length > 0 && (
          <div className="pt-2">
            <p className="text-xs text-muted-foreground mb-2">Categories</p>
            <div className="flex flex-wrap gap-2">
              {tokenInfo.categories.slice(0, 5).map((cat, idx) => {
                const label = typeof cat === 'string' ? cat : (cat as any)?.name || (cat as any)?.category || String(cat);
                return (
                  <Badge key={label + idx} variant="secondary" className="text-xs">
                    {label}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
